import {
  ChangeEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import MenuItem from "@mui/material/MenuItem";
import { alpha } from "@mui/material/styles";
import { visuallyHidden } from "@mui/utils";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";

import { useAppSelector } from "@store/hooks";
import {
  addWorkspaceMember,
  getWorkspaceData,
  removeWorkspaceMember,
  updateWorkspaceMemberRole,
} from "@services/api";
import WorkspaceInviteLinksPanel from "./WorkspaceInviteLinksPanel";
import {
  getComparator,
  stableSort,
  parseDate,
} from "@features/documents/components/table-functions";
import type { Order } from "@features/documents/components/EnhancedTableHead";
import type { RootState } from "@store/index";
import type {
  Workspace,
  WorkspacePermissionWithAuthor,
} from "@/types/models";

type Role = "owner" | "contributor";
type SortKey = "username" | "role" | "createdAt";

interface MemberRow {
  permissionId: string;
  userId: string;
  username: string;
  // Falls back to the UUID when email hasn't been backfilled yet, so
  // every row still shows a stable identifier.
  email: string;
  role: Role;
  createdAt: string;
  addedBy: string | null;
}

interface HeadCell {
  id: SortKey;
  label: string;
  numeric: boolean;
}

const headCells: readonly HeadCell[] = [
  { id: "username", label: "Member", numeric: false },
  { id: "role", label: "Role", numeric: false },
  { id: "createdAt", label: "Joined", numeric: true },
];

const toMemberRow = (p: WorkspacePermissionWithAuthor): MemberRow => ({
  permissionId: p.workspace_permission_id,
  userId: p.user_id,
  username: p.member?.username || p.user_id,
  email: p.member?.email || p.user_id,
  role: (p.permission_name === "owner" ? "owner" : "contributor") as Role,
  createdAt: p.created_at,
  addedBy: p.author?.username ?? null,
});

export default function MembersTable() {
  const workspaceId = useAppSelector(
    (state: RootState) => state.workspace.workspace_id
  );
  const currentUserId = useAppSelector(
    (state: RootState) => state.user.userId
  );

  const [members, setMembers] = useState<MemberRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<SortKey>("username");
  const [selected, setSelected] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [bulkPending, setBulkPending] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [addEmail, setAddEmail] = useState("");
  const [addRole, setAddRole] = useState<Role>("contributor");
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [addError, setAddError] = useState<string>("");

  const reload = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    setError("");
    const result = await getWorkspaceData(workspaceId);
    setLoading(false);
    if (!result.success) {
      setError(result.error.message);
      return;
    }
    const ws = result.data as Workspace;
    setMembers((ws.workspace_permissions ?? []).map(toMemberRow));
  }, [workspaceId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const ownerCount = useMemo(
    () => members.filter((m) => m.role === "owner").length,
    [members]
  );

  const filteredMembers = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return members;
    return members.filter(
      (m) =>
        m.username.toLowerCase().includes(term) ||
        m.email.toLowerCase().includes(term) ||
        (m.addedBy ?? "").toLowerCase().includes(term)
    );
  }, [members, search]);

  const sortedRows = useMemo(
    () =>
      stableSort(
        filteredMembers as Array<MemberRow & Record<SortKey, string | null>>,
        getComparator(order, orderBy)
      ),
    [filteredMembers, order, orderBy]
  );

  const pagedRows = useMemo(
    () => sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedRows, page, rowsPerPage]
  );

  const handleRequestSort = (property: SortKey) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected(
        filteredMembers
          .filter((m) => m.userId !== currentUserId)
          .map((m) => m.permissionId)
      );
      return;
    }
    setSelected([]);
  };

  const handleRowToggle = (_e: MouseEvent<unknown>, permissionId: string) => {
    setSelected((prev) =>
      prev.includes(permissionId)
        ? prev.filter((x) => x !== permissionId)
        : [...prev, permissionId]
    );
  };

  const handleRoleChange = async (row: MemberRow, next: Role) => {
    if (next === row.role) return;
    setPendingId(row.permissionId);
    setError("");
    const result = await updateWorkspaceMemberRole(workspaceId, row.userId, next);
    setPendingId(null);
    if (!result.success) {
      setError(result.error.message);
      return;
    }
    await reload();
  };

  const handleRemove = async (row: MemberRow) => {
    if (
      !window.confirm(
        `Remove ${row.username} from this workspace? Their permissions on workspace resources will be revoked.`
      )
    ) {
      return;
    }
    setPendingId(row.permissionId);
    setError("");
    const result = await removeWorkspaceMember(workspaceId, row.userId);
    setPendingId(null);
    if (!result.success) {
      setError(result.error.message);
      return;
    }
    setSelected((prev) => prev.filter((x) => x !== row.permissionId));
    await reload();
  };

  const handleBulkRemove = async () => {
    const targets = members.filter(
      (m) => selected.includes(m.permissionId) && m.userId !== currentUserId
    );
    if (targets.length === 0) return;
    if (
      !window.confirm(
        `Remove ${targets.length} member${
          targets.length === 1 ? "" : "s"
        }? Their permissions on workspace resources will be revoked.`
      )
    ) {
      return;
    }
    setBulkPending(true);
    setError("");
    const results = await Promise.allSettled(
      targets.map((t) => removeWorkspaceMember(workspaceId, t.userId))
    );
    setBulkPending(false);
    const failures = results.filter(
      (r) => r.status === "rejected" || (r.status === "fulfilled" && !r.value.success)
    );
    if (failures.length > 0) {
      setError(
        `Failed to remove ${failures.length} of ${targets.length} members.`
      );
    }
    setSelected([]);
    await reload();
  };

  const resetAddDialog = () => {
    setAddEmail("");
    setAddRole("contributor");
    setAddError("");
  };
  const closeAddDialog = () => {
    if (addSubmitting) return;
    setAddOpen(false);
    resetAddDialog();
  };
  const handleAdd = async () => {
    const trimmed = addEmail.trim();
    if (!trimmed) {
      setAddError("Email is required");
      return;
    }
    setAddSubmitting(true);
    setAddError("");
    const result = await addWorkspaceMember(
      workspaceId,
      { email: trimmed },
      addRole
    );
    setAddSubmitting(false);
    if (!result.success) {
      setAddError(result.error.message);
      return;
    }
    setAddOpen(false);
    resetAddDialog();
    await reload();
  };

  const numSelected = selected.length;
  const selectableCount = filteredMembers.filter(
    (m) => m.userId !== currentUserId
  ).length;
  const emptyRows =
    page > 0
      ? Math.max(0, (1 + page) * rowsPerPage - filteredMembers.length)
      : 0;

  return (
    <Box sx={{ width: "100%", p: 4 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {workspaceId && <WorkspaceInviteLinksPanel workspaceId={workspaceId} />}

      <Paper sx={{ width: "100%", mb: 2 }}>
        <Toolbar
          sx={{
            pl: { sm: 2 },
            pr: { xs: 1, sm: 1 },
            gap: 2,
            flexWrap: "wrap",
            ...(numSelected > 0 && {
              bgcolor: (theme) =>
                alpha(
                  theme.palette.secondary.main,
                  theme.palette.action.activatedOpacity
                ),
            }),
          }}
        >
          {numSelected > 0 ? (
            <Typography variant="h6" sx={{ flex: "1 1 auto" }}>
              {numSelected} selected
            </Typography>
          ) : (
            <Typography variant="h6" sx={{ flex: "1 1 auto" }}>
              Members
            </Typography>
          )}

          {numSelected > 0 ? (
            <Tooltip title="Remove selected">
              <span>
                <IconButton onClick={handleBulkRemove} disabled={bulkPending}>
                  {bulkPending ? <CircularProgress size={20} /> : <DeleteIcon />}
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            <>
              <TextField
                label="Search"
                variant="outlined"
                size="small"
                value={search}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                sx={{ width: "28ch" }}
              />
              <Button
                variant="contained"
                size="small"
                startIcon={<PersonAddAltOutlinedIcon />}
                onClick={() => setAddOpen(true)}
              >
                Add member
              </Button>
            </>
          )}
        </Toolbar>

        <TableContainer>
          <Table sx={{ minWidth: 600 }} size="medium">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    indeterminate={
                      numSelected > 0 && numSelected < selectableCount
                    }
                    checked={
                      selectableCount > 0 && numSelected === selectableCount
                    }
                    onChange={handleSelectAllClick}
                    inputProps={{ "aria-label": "select all members" }}
                    disabled={selectableCount === 0}
                  />
                </TableCell>
                {headCells.map((headCell) => (
                  <TableCell
                    key={headCell.id}
                    align={headCell.numeric ? "right" : "left"}
                    sortDirection={orderBy === headCell.id ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? order : "asc"}
                      onClick={() => handleRequestSort(headCell.id)}
                    >
                      {headCell.label}
                      {orderBy === headCell.id ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && members.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : pagedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      No members match your search.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                pagedRows.map((row, idx) => {
                  const isItemSelected = selected.includes(row.permissionId);
                  const isSelf = row.userId === currentUserId;
                  const isLastOwner = row.role === "owner" && ownerCount <= 1;
                  const isPending = pendingId === row.permissionId;
                  const labelId = `members-row-${idx}`;
                  return (
                    <TableRow
                      key={row.permissionId}
                      hover
                      role="checkbox"
                      aria-checked={isItemSelected}
                      selected={isItemSelected}
                      tabIndex={-1}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          checked={isItemSelected}
                          onClick={(e) => handleRowToggle(e, row.permissionId)}
                          inputProps={{ "aria-labelledby": labelId }}
                          disabled={isSelf}
                        />
                      </TableCell>
                      <TableCell id={labelId}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body1" fontWeight={500}>
                            {row.username}
                          </Typography>
                          {isSelf && (
                            <Chip label="You" size="small" variant="outlined" />
                          )}
                        </Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block" }}
                        >
                          {row.email}
                          {row.addedBy ? ` · added by ${row.addedBy}` : ""}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                          <Select
                            value={row.role}
                            onChange={(e: SelectChangeEvent) =>
                              handleRoleChange(row, e.target.value as Role)
                            }
                            disabled={isPending || isLastOwner}
                          >
                            <MenuItem value="contributor">Contributor</MenuItem>
                            <MenuItem value="owner">Owner</MenuItem>
                          </Select>
                        </FormControl>
                        {isLastOwner && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mt: 0.5 }}
                          >
                            Only owner — promote someone else first.
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {parseDate(row.createdAt)}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip
                          title={
                            isSelf
                              ? "You can't remove yourself"
                              : isLastOwner
                                ? "Can't remove the last owner"
                                : "Remove"
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleRemove(row)}
                              disabled={isSelf || isLastOwner || isPending}
                              sx={{
                                color: "error.main",
                                "&.Mui-disabled": {
                                  color: "error.light",
                                  opacity: 0.5,
                                },
                              }}
                            >
                              {isPending ? (
                                <CircularProgress size={18} />
                              ) : (
                                <DeleteIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
              {emptyRows > 0 && pagedRows.length > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={5} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredMembers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      <Dialog open={addOpen} onClose={closeAddDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add member</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add a registered user by email. For onboarding strangers, share
            an invite link instead — see the panel above.
          </Typography>
          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
            <TextField
              fullWidth
              size="small"
              type="email"
              label="Email"
              placeholder="name@example.com"
              value={addEmail}
              onChange={(e) => setAddEmail(e.target.value)}
              disabled={addSubmitting}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="add-member-role-label">Role</InputLabel>
              <Select
                labelId="add-member-role-label"
                label="Role"
                value={addRole}
                onChange={(e: SelectChangeEvent) =>
                  setAddRole(e.target.value as Role)
                }
                disabled={addSubmitting}
              >
                <MenuItem value="contributor">Contributor</MenuItem>
                <MenuItem value="owner">Owner</MenuItem>
              </Select>
            </FormControl>
          </Box>
          {addError && (
            <Typography variant="body2" color="error" sx={{ mt: 1.5 }}>
              {addError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAddDialog} disabled={addSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={addSubmitting || !addEmail.trim()}
            startIcon={addSubmitting ? <CircularProgress size={16} /> : null}
          >
            {addSubmitting ? "Adding…" : "Add member"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
